import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { DISTRICTS, districtLabel } from '../../lib/districts';
import type { Facility, FacilityAvailability, FacilityBooking, FacilityRecurringSlot, FacilityType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, SectionCard, Spinner, StatusBadge, Tabs } from '../../ui/components';

const FACILITY_TYPES: FacilityType[] = ['STADIUM', 'COURT', 'HALL', 'POOL', 'PITCH', 'TRACK', 'GYM', 'CULTURAL_CENTRE', 'COMMUNITY_HALL'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TABS = [
  { key: 'catalogue', label: 'Facility catalogue' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'recurring', label: 'Recurring slots' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function FacilitiesPage() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('FACILITY_MANAGER') ?? false;
  const [tab, setTab] = useState<TabKey>('catalogue');

  // Loaded once, shared across every tab so bookings/recurring-slot rows can
  // resolve a facilityId to a human name without a second round-trip.
  const facilitiesQ = useApiQuery<Page<Facility> | { items: Facility[]; nextCursor: null }>(
    canManage ? '/api/v1/m30/facilities/admin?limit=100' : '/api/v1/m30/facilities?limit=100',
  );
  const facilities = useMemo(() => facilitiesQ.data?.items ?? [], [facilitiesQ.data]);
  const facilityName = (id: string) => facilities.find((f) => f.id === id)?.name ?? id;

  return (
    <div>
      <PageHeader
        title="Facility Booking Management"
        subtitle="Bookable venues, ad-hoc bookings, and recurring club slot allocations — packages/modules/m30-youth-sports facilities.ts + bookings.ts."
      />
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Signed in without the Facility Manager role — showing the public catalogue only. Booking admin, approvals, and edits require FACILITY_MANAGER." />
        </div>
      ) : null}
      {tab === 'catalogue' ? <CatalogueTab canManage={canManage} facilitiesQ={facilitiesQ} facilities={facilities} /> : null}
      {tab === 'bookings' ? <BookingsTab canManage={canManage} facilityName={facilityName} /> : null}
      {tab === 'recurring' ? <RecurringTab canManage={canManage} facilityName={facilityName} /> : null}
    </div>
  );
}

// ── Catalogue ──────────────────────────────────────────────────────────

function CatalogueTab({
  canManage,
  facilitiesQ,
  facilities,
}: {
  canManage: boolean;
  facilitiesQ: ReturnType<typeof useApiQuery<Page<Facility> | { items: Facility[]; nextCursor: null }>>;
  facilities: Facility[];
}) {
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = facilities.find((f) => f.id === selectedId) ?? null;

  const filtered = facilities.filter((f) => !q.trim() || `${f.name} ${f.description ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="grid gap-5 lg:grid-cols-5">
      <div className="lg:col-span-3">
        <div className="mb-3 flex items-center gap-2">
          <input className="field-input" placeholder="Filter by name or description…" value={q} onChange={(e) => setQ(e.target.value)} />
          {canManage ? (
            <button className="btn-primary shrink-0" onClick={() => setShowCreate(true)}>
              + New facility
            </button>
          ) : null}
        </div>
        {facilitiesQ.error ? <ErrorNote message={facilitiesQ.error} /> : null}
        <div className="card overflow-x-auto">
          {facilitiesQ.loading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <EmptyState title="No facilities found" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Name</th>
                  <th className="table-head">Type</th>
                  <th className="table-head">District</th>
                  <th className="table-head text-right">Rate / hr</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => (
                  <tr
                    key={f.id}
                    className={`cursor-pointer hover:bg-slate-50 ${selectedId === f.id ? 'bg-brand-50/60' : ''}`}
                    onClick={() => setSelectedId(f.id)}
                  >
                    <td className="table-cell font-medium">{f.name}</td>
                    <td className="table-cell text-xs text-slate-500">{f.type.replaceAll('_', ' ')}</td>
                    <td className="table-cell text-xs text-slate-500">{districtLabel(f.district)}</td>
                    <td className="table-cell text-right tabular-nums">{scr(f.hourlyRateScr)}</td>
                    <td className="table-cell">
                      <StatusBadge status={f.active ? 'ACTIVE' : 'INACTIVE'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="lg:col-span-2">
        {selected ? (
          <FacilityDetail key={selected.id} facility={selected} canManage={canManage} onChanged={facilitiesQ.reload} />
        ) : (
          <SectionCard>
            <EmptyState title="Select a facility" hint="Pick a row to view availability, book a slot, or edit its catalogue entry." />
          </SectionCard>
        )}
      </div>

      {showCreate ? (
        <CreateFacilityModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            facilitiesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FacilityDetail({ facility, canManage, onChanged }: { facility: Facility; canManage: boolean; onChanged: () => void }) {
  const { persona } = useAuth();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const availabilityQ = useApiQuery<FacilityAvailability>(`/api/v1/m30/facilities/${facility.id}/availability?date=${date}`);

  const deactivate = async () => {
    if (!confirm(`Deactivate ${facility.name}? It will disappear from the public catalogue.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/facilities/${facility.id}/deactivate`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to deactivate');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title={facility.name}
        actions={
          canManage ? (
            <div className="flex gap-1.5">
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setEditing((v) => !v)}>
                {editing ? 'Close' : 'Edit'}
              </button>
              {facility.active ? (
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={deactivate} disabled={busy}>
                  Deactivate
                </button>
              ) : null}
            </div>
          ) : undefined
        }
      >
        {error ? (
          <div className="mb-2">
            <ErrorNote message={error} />
          </div>
        ) : null}
        {editing ? (
          <EditFacilityForm
            facility={facility}
            onDone={() => {
              setEditing(false);
              onChanged();
            }}
          />
        ) : (
          <dl className="space-y-1.5 text-sm">
            <Row label="Type" value={facility.type.replaceAll('_', ' ')} />
            <Row label="District" value={districtLabel(facility.district)} />
            <Row label="Address" value={facility.address ?? '—'} />
            <Row label="Capacity" value={facility.capacity ? `${facility.capacity.toLocaleString()} people` : '—'} />
            <Row label="Rate" value={scr(facility.hourlyRateScr)} />
            <Row label="Amenities" value={facility.amenities.join(', ') || '—'} />
            {facility.description ? <p className="pt-1 text-xs text-slate-500">{facility.description}</p> : null}
          </dl>
        )}
      </SectionCard>

      <SectionCard
        title="Day availability"
        actions={<input type="date" className="field-input !w-auto !py-1" value={date} onChange={(e) => setDate(e.target.value)} />}
      >
        {availabilityQ.loading ? (
          <Spinner label="Checking…" />
        ) : availabilityQ.error ? (
          <ErrorNote message={availabilityQ.error} />
        ) : (availabilityQ.data?.bookedSlots.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">Nothing booked that day — the whole facility is open.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {availabilityQ.data!.bookedSlots.map((b) => (
              <li key={b.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5">
                <span className="tabular-nums">
                  {formatDateTime(b.startsAt)} – {new Date(b.endsAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                </span>
                <span className="flex items-center gap-2">
                  {b.clubName ? <span className="text-xs text-slate-500">{b.clubName}</span> : null}
                  <StatusBadge status={b.status} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <NewBookingForm facilityId={facility.id} onBooked={() => availabilityQ.reload()} />
      <NewRecurringSlotForm facilityId={facility.id} />

      {persona ? (
        <p className="text-[11px] text-slate-400">
          Booking/recurring-slot requests below are submitted as the signed-in persona ({persona.name}) — m30 derives the
          booker identity from the caller's own token with no separate "book on behalf of" field (see build report).
        </p>
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-800">{value}</dd>
    </div>
  );
}

function EditFacilityForm({ facility, onDone }: { facility: Facility; onDone: () => void }) {
  const [name, setName] = useState(facility.name);
  const [description, setDescription] = useState(facility.description ?? '');
  const [hourlyRateScr, setHourlyRateScr] = useState(String(facility.hourlyRateScr));
  const [capacity, setCapacity] = useState(facility.capacity ? String(facility.capacity) : '');
  const [amenities, setAmenities] = useState(facility.amenities.join(', '));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m30/facilities/${facility.id}`, {
        name,
        description: description || undefined,
        hourlyRateScr: Number.parseFloat(hourlyRateScr) || 0,
        capacity: capacity ? Number.parseInt(capacity, 10) : undefined,
        amenities: amenities.split(',').map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5">
      {error ? <ErrorNote message={error} /> : null}
      <Field label="Name">
        <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description">
        <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-2.5">
        <Field label="Rate / hr (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={hourlyRateScr} onChange={(e) => setHourlyRateScr(e.target.value)} />
        </Field>
        <Field label="Capacity">
          <input className="field-input" type="number" min="0" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
        </Field>
      </div>
      <Field label="Amenities (comma separated)">
        <input className="field-input" value={amenities} onChange={(e) => setAmenities(e.target.value)} />
      </Field>
      <button className="btn-primary w-full" onClick={submit} disabled={busy || name.trim().length < 2}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}

function CreateFacilityModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState<FacilityType>('HALL');
  const [district, setDistrict] = useState<string>(DISTRICTS[0]);
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [capacity, setCapacity] = useState('');
  const [hourlyRateScr, setHourlyRateScr] = useState('0');
  const [amenities, setAmenities] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m30/facilities', {
        name,
        type,
        district,
        address: address || undefined,
        description: description || undefined,
        capacity: capacity ? Number.parseInt(capacity, 10) : undefined,
        hourlyRateScr: Number.parseFloat(hourlyRateScr) || 0,
        amenities: amenities.split(',').map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create facility');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New facility" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anse Boileau Tennis Courts" />
          </Field>
          <Field label="Type">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as FacilityType)}>
              {FACILITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="District">
            <select className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)}>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {districtLabel(d)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Address">
            <input className="field-input" value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>
        </div>
        <Field label="Description">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Capacity">
            <input className="field-input" type="number" min="0" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
          <Field label="Rate / hr (SCR, 0 = free)">
            <input className="field-input" type="number" min="0" step="0.01" value={hourlyRateScr} onChange={(e) => setHourlyRateScr(e.target.value)} />
          </Field>
        </div>
        <Field label="Amenities (comma separated)">
          <input className="field-input" value={amenities} onChange={(e) => setAmenities(e.target.value)} placeholder="floodlights, changing-rooms" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || name.trim().length < 2}>
            {busy ? 'Creating…' : 'Create facility'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function NewBookingForm({ facilityId, onBooked }: { facilityId: string; onBooked: () => void }) {
  const { persona } = useAuth();
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [clubName, setClubName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<FacilityBooking | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const booking = await api.post<FacilityBooking>(`/api/v1/m30/facilities/${facilityId}/bookings`, {
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        clubName: clubName || undefined,
        purpose: purpose || undefined,
      });
      setOk(booking);
      onBooked();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Booking failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="New ad-hoc booking">
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {ok ? (
        <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          Booked — status <strong>{ok.status}</strong>, fee {scr(ok.feeAmount)}.
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-2.5">
        <Field label="Starts">
          <input type="datetime-local" className="field-input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </Field>
        <Field label="Ends">
          <input type="datetime-local" className="field-input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </Field>
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-2.5">
        <Field label="Club name (optional)">
          <input className="field-input" value={clubName} onChange={(e) => setClubName(e.target.value)} />
        </Field>
        <Field label="Purpose (optional)">
          <input className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-3 w-full" onClick={submit} disabled={busy || !startsAt || !endsAt || !persona}>
        {busy ? 'Booking…' : `Book as ${persona?.name ?? 'yourself'}`}
      </button>
    </SectionCard>
  );
}

function NewRecurringSlotForm({ facilityId }: { facilityId: string }) {
  const { persona } = useAuth();
  const [clubName, setClubName] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('2');
  const [startTime, setStartTime] = useState('17:00');
  const [endTime, setEndTime] = useState('19:00');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() => new Date(Date.now() + 60 * 24 * 3600_000).toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<FacilityRecurringSlot | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const slot = await api.post<FacilityRecurringSlot>(`/api/v1/m30/facilities/${facilityId}/recurring-slots`, {
        clubName,
        dayOfWeek: Number.parseInt(dayOfWeek, 10),
        startTime,
        endTime,
        startDate,
        endDate,
      });
      setOk(slot);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="New recurring club slot request">
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {ok ? (
        <div className="mb-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
          Request submitted — {DAY_NAMES[ok.dayOfWeek]}s {ok.startTime}–{ok.endTime}, status <strong>{ok.status}</strong>. Approve
          it from the Recurring Slots tab.
        </div>
      ) : null}
      <Field label="Club name">
        <input className="field-input" value={clubName} onChange={(e) => setClubName(e.target.value)} placeholder="e.g. Anse aux Pins FC" />
      </Field>
      <div className="mt-2.5 grid grid-cols-3 gap-2.5">
        <Field label="Day">
          <select className="field-input" value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
            {DAY_NAMES.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Start time">
          <input type="time" className="field-input" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
        </Field>
        <Field label="End time">
          <input type="time" className="field-input" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
        </Field>
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-2.5">
        <Field label="From">
          <input type="date" className="field-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Until">
          <input type="date" className="field-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
      </div>
      <button className="btn-secondary mt-3 w-full" onClick={submit} disabled={busy || clubName.trim().length < 2 || !persona}>
        {busy ? 'Submitting…' : `Request as ${persona?.name ?? 'yourself'}`}
      </button>
    </SectionCard>
  );
}

// ── Bookings ───────────────────────────────────────────────────────────

function BookingsTab({ canManage, facilityName }: { canManage: boolean; facilityName: (id: string) => string }) {
  const [status, setStatus] = useState('');
  const bookingsQ = useApiQuery<Page<FacilityBooking>>(
    canManage ? `/api/v1/m30/bookings?limit=50${status ? `&status=${status}` : ''}` : null,
  );

  if (!canManage) {
    return <ErrorNote message="Viewing all bookings requires the Facility Manager role." />;
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(['PENDING_PAYMENT', 'CONFIRMED', 'CANCELLED', 'REJECTED', 'COMPLETED'] as const).map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button className="btn-secondary" onClick={bookingsQ.reload}>
          Refresh
        </button>
      </div>
      {bookingsQ.error ? <ErrorNote message={bookingsQ.error} /> : null}
      <div className="card overflow-x-auto">
        {bookingsQ.loading ? (
          <Spinner />
        ) : (bookingsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No bookings" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Facility</th>
                <th className="table-head">Booker</th>
                <th className="table-head">Window</th>
                <th className="table-head text-right">Fee</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {bookingsQ.data!.items.map((b) => (
                <BookingRow key={b.id} booking={b} facilityName={facilityName} onChanged={bookingsQ.reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BookingRow({ booking, facilityName, onChanged }: { booking: FacilityBooking; facilityName: (id: string) => string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canCancel = (['PENDING_PAYMENT', 'CONFIRMED'] as const).includes(booking.status as never) && new Date(booking.startsAt).getTime() > Date.now();

  const cancel = async () => {
    const reason = prompt('Cancellation reason (optional):') ?? undefined;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/bookings/${booking.id}/cancel`, reason ? { reason } : {});
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50">
      <td className="table-cell font-medium">{facilityName(booking.facilityId)}</td>
      <td className="table-cell text-xs text-slate-500">
        {booking.bookerType} · {booking.clubName ?? booking.bookerId}
      </td>
      <td className="table-cell text-xs tabular-nums">{formatDateTime(booking.startsAt)}</td>
      <td className="table-cell text-right tabular-nums">{scr(booking.feeAmount)}</td>
      <td className="table-cell">
        <StatusBadge status={booking.status} />
        {error ? <div className="mt-1 text-[11px] text-rose-600">{error}</div> : null}
      </td>
      <td className="table-cell text-right">
        {canCancel ? (
          <button className="btn-secondary !px-2 !py-1 text-xs" onClick={cancel} disabled={busy}>
            Cancel
          </button>
        ) : null}
      </td>
    </tr>
  );
}

// ── Recurring slots ────────────────────────────────────────────────────

function RecurringTab({ canManage, facilityName }: { canManage: boolean; facilityName: (id: string) => string }) {
  const [status, setStatus] = useState('');
  const slotsQ = useApiQuery<Page<FacilityRecurringSlot>>(
    canManage ? `/api/v1/m30/recurring-slots?limit=50${status ? `&status=${status}` : ''}` : null,
  );

  if (!canManage) {
    return <ErrorNote message="Reviewing recurring-slot requests requires the Facility Manager role." />;
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button className="btn-secondary" onClick={slotsQ.reload}>
          Refresh
        </button>
      </div>
      {slotsQ.error ? <ErrorNote message={slotsQ.error} /> : null}
      <div className="card overflow-x-auto">
        {slotsQ.loading ? (
          <Spinner />
        ) : (slotsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No recurring-slot requests" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Facility</th>
                <th className="table-head">Club</th>
                <th className="table-head">Weekly window</th>
                <th className="table-head">Term</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {slotsQ.data!.items.map((s) => (
                <RecurringRow key={s.id} slot={s} facilityName={facilityName} onChanged={slotsQ.reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function RecurringRow({ slot, facilityName, onChanged }: { slot: FacilityRecurringSlot; facilityName: (id: string) => string; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<FacilityRecurringSlot>(`/api/v1/m30/recurring-slots/${slot.id}/approve`);
      setResult(`${updated.occurrencesCreated ?? 0} occurrence(s) created, ${updated.occurrencesSkipped ?? 0} skipped (clash).`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m30/recurring-slots/${slot.id}/reject`, { reason });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50">
      <td className="table-cell font-medium">{facilityName(slot.facilityId)}</td>
      <td className="table-cell text-xs text-slate-500">{slot.clubName}</td>
      <td className="table-cell text-xs tabular-nums">
        {DAY_NAMES[slot.dayOfWeek]}s {slot.startTime}–{slot.endTime}
      </td>
      <td className="table-cell text-xs tabular-nums">
        {new Date(slot.startDate).toLocaleDateString('en-GB', { timeZone: 'UTC' })} – {new Date(slot.endDate).toLocaleDateString('en-GB', { timeZone: 'UTC' })}
      </td>
      <td className="table-cell">
        <StatusBadge status={slot.status} />
        {result ? <div className="mt-1 text-[11px] text-emerald-700">{result}</div> : null}
        {error ? <div className="mt-1 text-[11px] text-rose-600">{error}</div> : null}
      </td>
      <td className="table-cell text-right">
        {slot.status === 'PENDING' ? (
          <div className="flex justify-end gap-1.5">
            <button className="btn-success !px-2 !py-1 text-xs" onClick={approve} disabled={busy}>
              Approve
            </button>
            <button className="btn-danger !px-2 !py-1 text-xs" onClick={reject} disabled={busy}>
              Reject
            </button>
          </div>
        ) : null}
      </td>
    </tr>
  );
}
